import React, { createContext, useCallback, useContext, useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, X } from "lucide-react";

const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);

  const dismiss = useCallback((id) => {
    setItems(xs => xs.filter(x => x.id !== id));
  }, []);

  const push = useCallback((kind, message, ms = 2800) => {
    const id = Math.random().toString(36).slice(2);
    setItems(xs => [...xs, { id, kind, message }]);
    setTimeout(() => dismiss(id), ms);
  }, [dismiss]);

  const api = {
    success: (m) => push("success", m),
    error:   (m) => push("error", m, 4200),
    warning: (m) => push("warning", m, 3500),
    info:    (m) => push("info", m),
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
           data-testid="toast-stack">
        {items.map(t => <ToastItem key={t.id} toast={t} onClose={() => dismiss(t.id)} />)}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastItem({ toast, onClose }) {
  const cfg = {
    success: { bg: "bg-emerald-600", Icon: CheckCircle2 },
    error:   { bg: "bg-red-600",     Icon: XCircle },
    warning: { bg: "bg-amber-500",   Icon: AlertTriangle },
    info:    { bg: "bg-slate-700",   Icon: CheckCircle2 },
  }[toast.kind] || { bg: "bg-slate-700", Icon: CheckCircle2 };
  const { bg, Icon } = cfg;
  return (
    <div
      className={`${bg} text-white rounded-xl shadow-lg pl-3 pr-2 py-2.5 min-w-[240px] max-w-sm flex items-start gap-2 pointer-events-auto animate-[toastIn_.2s_ease-out]`}
      data-testid={`toast-${toast.kind}`}
      role="status"
    >
      <Icon size={18} className="shrink-0 mt-0.5" />
      <div className="flex-1 text-sm leading-snug">{toast.message}</div>
      <button onClick={onClose} className="opacity-80 hover:opacity-100 shrink-0 p-1" aria-label="Dismiss">
        <X size={14}/>
      </button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}

/** Small helper - extract a user-friendly error message from any axios error. */
export function toastErrorMessage(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" · ");
  if (d?.msg) return d.msg;
  return "Something went wrong.";
}
